import React, { useEffect, useState } from 'react';
import { api } from '../App';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bell, Loader2 } from 'lucide-react';
import StudentVerificationCard from '../components/account/StudentVerificationCard';
import TermsReacceptBanner from '../components/account/TermsReacceptBanner';

/**
 * Minimal user settings surface. For now exposes:
 *   * Message email notifications mute toggle
 *   * (placeholder) SMS notifications, coming with Pro
 *
 * Extend this page as we add more per-user preferences (timezone,
 * language, two-factor toggle summary, etc.).
 */
export default function Settings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users/me/notification-prefs');
        setPrefs(res.data);
      } catch {
        toast.error('Could not load your notification settings.');
      }
    })();
  }, []);

  const toggle = async (key, value) => {
    const prev = prefs;
    setPrefs({ ...prefs, [key]: value });
    setSaving(true);
    try {
      await api.put('/users/me/notification-prefs', { [key]: value });
      toast.success('Preferences updated');
    } catch {
      setPrefs(prev);
      toast.error('Could not save, please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-light text-[#1A1A1A] mb-2">Settings</h1>
        <p className="text-stone-500 mb-8">Manage how Zuriya keeps you updated.</p>

        <div className="mb-6">
          <TermsReacceptBanner />
        </div>

        <div className="mb-6">
          <StudentVerificationCard />
        </div>

        <section className="bg-white rounded-2xl border border-[#E6E4DD] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="text-lg font-medium text-[#1A1A1A]">Notifications</h2>
          </div>

          {!prefs ? (
            <div className="flex items-center gap-2 text-sm text-stone-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading your preferences...
            </div>
          ) : (
            <div className="divide-y divide-[#F1EEE8]">
              <div className="flex items-center justify-between py-4">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-[#1A1A1A] font-medium">Message emails</p>
                  <p className="text-sm text-stone-500 mt-0.5">
                    Get a notification email when someone sends you a message and you're away from the app.
                  </p>
                </div>
                <Button
                  variant={prefs.messages_email ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggle('messages_email', !prefs.messages_email)}
                  disabled={saving}
                  className={prefs.messages_email ? 'btn-primary' : ''}
                  data-testid="toggle-messages-email"
                >
                  {prefs.messages_email ? 'On' : 'Off'}
                </Button>
              </div>
              <div className="flex items-center justify-between py-4 opacity-60">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-[#1A1A1A] font-medium">Message SMS</p>
                  <p className="text-sm text-stone-500 mt-0.5">
                    Text alerts for new messages. Available with Pro, coming soon.
                  </p>
                </div>
                <Button variant="outline" size="sm" disabled>Coming soon</Button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
